import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Button,
  Divider,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import {
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  CreditCard as CreditCardIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Info as InfoIcon,
} from '@mui/icons-material'
import { CompanyUser } from '../../core/models/Company'
import { useRbacStore } from '../../stores/rbacStore'
import { memberRoleLabel } from '../CompanyManagement/utils/roleOptions'
import { CompanyWithStats } from '../CompanyManagement/types'
import {
  StyledDialog,
  StyledDialogTitle,
  StyledDialogContent,
  StyledDialogActions,
  StyledButton,
} from '../CompanyManagement/styles/StyledComponents'
import { useTheme } from '@mui/material/styles'
import { alpha } from '@mui/material/styles'

interface BillingImpact {
  current_users: number
  new_users: number
  current_cost: number
  new_cost: number
  cost_difference: number
  prorated_amount: number
  billing_action: string
  billing_cycle: 'monthly' | 'yearly'
  credit_balance?: number
  stripe_balance_available?: boolean
  credit_used?: number
  remaining_charge?: number
  tier_info?: {
    name: string
    tier_key: string
    base_price: number
    price_per_user: number
    free_users_limit: number
  }
  grace_period_info?: {
    hours_remaining: number
    credit_amount: number
  }
}

interface BillingImpactPreviewProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  action: 'addition' | 'removal'
  user?: CompanyUser
  billingImpact: BillingImpact | null
  company: CompanyWithStats | null
  loading?: boolean
  isBulkOperation?: boolean
  selectedUserIds?: string[]
}

export const BillingImpactPreview: React.FC<BillingImpactPreviewProps> = ({
  open,
  onClose,
  onConfirm,
  action,
  user,
  billingImpact,
  company,
  loading = false,
  isBulkOperation = false,
  selectedUserIds = [],
}) => {
  const theme = useTheme()
  const [confirming, setConfirming] = useState(false)
  // Names the screen already holds, for rows that carry a key but no name; never fetched here.
  const knownRoles = useRbacStore((s) => s.catalogue?.systemRoles)

  // Helper function to safely format currency values
  const formatCurrency = (value: number | undefined | null, defaultValue: number = 0): string => {
    const numValue = value ?? defaultValue
    return numValue.toFixed(2)
  }

  // Early return if billingImpact is null or undefined
  if (!billingImpact) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Billing Impact Preview</DialogTitle>
        <DialogContent>
          <Typography>No billing impact data available.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    )
  }

  const handleConfirm = async () => {
    console.log('BillingImpactPreview: handleConfirm called')
    console.log('Action:', action)
    console.log('User:', user)
    console.log('BillingImpact:', billingImpact)
    
    setConfirming(true)
    try {
      await onConfirm()
    } finally {
      setConfirming(false)
    }
  }

  const getActionIcon = () => {
    switch (action) {
      case 'addition':
        return <TrendingUpIcon sx={{ color: 'success.main' }} />
      case 'removal':
        return <TrendingDownIcon sx={{ color: 'warning.main' }} />
      default:
        return <InfoIcon sx={{ color: 'info.main' }} />
    }
  }

  const getBillingActionMessage = () => {
    if (!billingImpact) return ''

    const userCount = isBulkOperation ? selectedUserIds.length : 1
    const userText = userCount === 1 ? 'user' : 'users'

    switch (billingImpact.billing_action) {
      case 'immediate_proration':
        return `This will result in an immediate prorated charge of $${formatCurrency(billingImpact.prorated_amount)} for ${userCount} ${userText}`
      case 'immediate_credit':
        return `This will result in an immediate credit of $${formatCurrency(billingImpact.prorated_amount)} for ${userCount} ${userText}`
      case 'grace_period_credit':
        return `This will result in a credit of $${formatCurrency(billingImpact.prorated_amount)} after the grace period for ${userCount} ${userText}`
      case 'no_immediate_billing':
        return `This change will be reflected in your next billing cycle for ${userCount} ${userText}`
      case 'no_billing_impact':
        return `This change has no billing impact for ${userCount} ${userText}`
      default:
        return `Billing impact calculated for ${userCount} ${userText}`
    }
  }

  const getBillingActionSeverity = () => {
    if (!billingImpact) return 'info'

    switch (billingImpact.billing_action) {
      case 'immediate_proration':
        return 'warning'
      case 'immediate_credit':
        return 'success'
      case 'grace_period_credit':
        return 'info'
      case 'no_immediate_billing':
        return 'success'
      case 'no_billing_impact':
        return 'success'
      default:
        return 'info'
    }
  }

  if (loading) {
    return (
      <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <StyledDialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
            <CircularProgress sx={{ mr: 2 }} />
            <Typography sx={{ color: 'text.secondary' }}>
              Calculating billing impact...
            </Typography>
          </Box>
        </StyledDialogContent>
      </StyledDialog>
    )
  }

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <StyledDialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {getActionIcon()}
          <Typography variant="h6" sx={{ color: 'text.primary' }}>
            {isBulkOperation 
              ? `${action === 'addition' ? 'Add' : 'Remove'} ${selectedUserIds.length} Users - Billing Impact`
              : `${action === 'addition' ? 'Add User' : 'Remove User'} - Billing Impact`
            }
          </Typography>
        </Box>
      </StyledDialogTitle>

      <StyledDialogContent>
        {billingImpact && (
          <Box sx={{ p: 2 }}>
            {/* User Information */}
            {!isBulkOperation && user && (
              <Card sx={{ mb: 3, bgcolor: 'background.paper', backgroundImage: 'none', border: 1, borderColor: 'divider' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ color: 'text.primary', mb: 2 }}>
                    User Information
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box>
                      <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 500 }}>
                        {user.profile?.firstName && user.profile?.lastName
                          ? `${user.profile.firstName} ${user.profile.lastName}`
                          : user.user?.username || user.user?.email}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {user.user?.email || 'No email'}
                      </Typography>
                    </Box>
                    <Chip
                      label={memberRoleLabel(user, user.role, knownRoles)}
                      size="small"
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.2),
                        color: 'primary.main',
                        fontSize: '0.75rem',
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Bulk Operation Information */}
            {isBulkOperation && (
              <Card sx={{ mb: 3, bgcolor: 'background.paper', backgroundImage: 'none', border: 1, borderColor: 'divider' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ color: 'text.primary', mb: 2 }}>
                    Bulk Operation Information
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box>
                      <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 500 }}>
                        {selectedUserIds.length} user{selectedUserIds.length !== 1 ? 's' : ''} selected for {action}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        This operation will affect {selectedUserIds.length} user{selectedUserIds.length !== 1 ? 's' : ''} in your company
                      </Typography>
                    </Box>
                    <Chip
                      label={`${selectedUserIds.length} Users`}
                      size="small"
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.2),
                        color: 'primary.main',
                        fontSize: '0.75rem',
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Billing Impact Summary */}
            <Alert severity={getBillingActionSeverity()} sx={{ mb: 3 }}>
              <Typography variant="body2">
                {getBillingActionMessage()}
              </Typography>
              {action === 'removal' && (
                <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                  Click "Remove User" below to confirm the removal and apply the credit.
                </Typography>
              )}
            </Alert>

            {/* Current vs New Billing Status */}
            <Card sx={{ mb: 3, bgcolor: 'background.paper', backgroundImage: 'none', border: 1, borderColor: 'divider' }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: 'text.primary', mb: 2 }}>
                  Billing Impact Summary
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Current Users
                      </Typography>
                      <Typography variant="h4" sx={{ color: 'text.primary', fontWeight: 'bold' }}>
                        {billingImpact.current_users}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        New Users
                      </Typography>
                      <Typography variant="h4" sx={{ color: 'text.primary', fontWeight: 'bold' }}>
                        {billingImpact.new_users}
                        {billingImpact.new_users > billingImpact.current_users && (
                          <Chip
                            label={`+${billingImpact.new_users - billingImpact.current_users}`}
                            size="small"
                            sx={{ ml: 1, bgcolor: alpha(theme.palette.success.main, 0.2), color: 'success.main' }}
                          />
                        )}
                        {billingImpact.new_users < billingImpact.current_users && (
                          <Chip
                            label={`${billingImpact.new_users - billingImpact.current_users}`}
                            size="small"
                            sx={{ ml: 1, bgcolor: alpha(theme.palette.warning.main, 0.2), color: 'warning.main' }}
                          />
                        )}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2, borderColor: 'divider' }} />

                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Current Cost
                      </Typography>
                      <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 'bold' }}>
                        ${formatCurrency(billingImpact.current_cost)}
                        <Typography component="span" variant="body2" sx={{ color: 'text.secondary' }}>
                          /{billingImpact.billing_cycle}
                        </Typography>
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        New Cost
                      </Typography>
                      <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 'bold' }}>
                        ${formatCurrency(billingImpact.new_cost)}
                        <Typography component="span" variant="body2" sx={{ color: 'text.secondary' }}>
                          /{billingImpact.billing_cycle}
                        </Typography>
                        {billingImpact.cost_difference > 0 && (
                          <Chip
                            label={`+$${formatCurrency(billingImpact.cost_difference)}`}
                            size="small"
                            sx={{ ml: 1, bgcolor: alpha(theme.palette.error.main, 0.2), color: 'error.main' }}
                          />
                        )}
                        {billingImpact.cost_difference < 0 && (
                          <Chip
                            label={`-$${formatCurrency(Math.abs(billingImpact.cost_difference))}`}
                            size="small"
                            sx={{ ml: 1, bgcolor: alpha(theme.palette.success.main, 0.2), color: 'success.main' }}
                          />
                        )}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Prorated vs Monthly Cost Breakdown */}
            {action === 'addition' && billingImpact.cost_difference > 0 && (
              <Card sx={{ mb: 3, bgcolor: alpha(theme.palette.warning.main, 0.1), border: `1px solid ${alpha(theme.palette.warning.main, 0.35)}` }}>
                <CardContent>
                  <Typography variant="h6" sx={{ color: 'warning.main', mb: 2, display: 'flex', alignItems: 'center' }}>
                    <MoneyIcon sx={{ mr: 1 }} />
                    Cost Breakdown
                  </Typography>
                  
                  <Grid container spacing={3}>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center', p: 2, bgcolor: alpha(theme.palette.warning.main, 0.1), borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                          Prorated Cost (Now)
                        </Typography>
                        <Typography variant="h4" sx={{ color: 'warning.main', fontWeight: 'bold' }}>
                          ${formatCurrency(billingImpact.prorated_amount)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 1 }}>
                          For remaining billing period
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center', p: 2, bgcolor: alpha(theme.palette.primary.main, 0.1), borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                          {billingImpact.billing_cycle === 'yearly' ? 'Yearly' : 'Monthly'} Cost (Next Cycle)
                        </Typography>
                        <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                          ${formatCurrency(billingImpact.cost_difference)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 1 }}>
                          Per {billingImpact.billing_cycle}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                  
                  <Alert severity="info" sx={{ mt: 2, bgcolor: alpha(theme.palette.info.main, 0.08), border: `1px solid ${alpha(theme.palette.info.main, 0.35)}` }}>
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>
                      <strong>Prorated Cost:</strong> You'll be charged ${formatCurrency(billingImpact.prorated_amount)} immediately for the remaining days in your current billing cycle.
                      <br />
                      <strong>{billingImpact.billing_cycle === 'yearly' ? 'Yearly' : 'Monthly'} Cost:</strong> Starting next billing cycle, you'll be charged ${formatCurrency(billingImpact.cost_difference)} per {billingImpact.billing_cycle}.
                    </Typography>
                  </Alert>
                </CardContent>
              </Card>
            )}

            {/* Credit Breakdown for User Removal */}
            {action === 'removal' && billingImpact.cost_difference < 0 && (
              <Card sx={{ mb: 3, bgcolor: alpha(theme.palette.success.main, 0.1), border: `1px solid ${alpha(theme.palette.success.main, 0.35)}` }}>
                <CardContent>
                  <Typography variant="h6" sx={{ color: 'success.main', mb: 2, display: 'flex', alignItems: 'center' }}>
                    <CreditCardIcon sx={{ mr: 1 }} />
                    Credit Breakdown
                  </Typography>
                  
                  <Grid container spacing={3}>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center', p: 2, bgcolor: alpha(theme.palette.success.main, 0.1), borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                          Immediate Credit
                        </Typography>
                        <Typography variant="h4" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                          ${formatCurrency(Math.abs(billingImpact.prorated_amount || 0))}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 1 }}>
                          Added to your account now
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center', p: 2, bgcolor: alpha(theme.palette.primary.main, 0.1), borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                          {billingImpact.billing_cycle === 'yearly' ? 'Yearly' : 'Monthly'} Savings
                        </Typography>
                        <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                          ${formatCurrency(Math.abs(billingImpact.cost_difference))}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 1 }}>
                          Per {billingImpact.billing_cycle}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                  
                  <Alert severity="success" sx={{ mt: 2, bgcolor: alpha(theme.palette.success.main, 0.08), border: `1px solid ${alpha(theme.palette.success.main, 0.35)}` }}>
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>
                      <strong>Immediate Credit:</strong> You'll receive ${formatCurrency(Math.abs(billingImpact.prorated_amount || 0))} credit immediately for the remaining days in your current billing cycle.
                      <br />
                      <strong>{billingImpact.billing_cycle === 'yearly' ? 'Yearly' : 'Monthly'} Savings:</strong> Starting next billing cycle, you'll save ${formatCurrency(Math.abs(billingImpact.cost_difference))} per {billingImpact.billing_cycle}.
                    </Typography>
                  </Alert>
                </CardContent>
              </Card>
            )}

            {/* Credit Information (Stripe ONLY) */}
            {(() => {
              const stripeCredit = billingImpact.stripe_balance_available
                ? (billingImpact.credit_balance || 0)
                : 0;
              return (
                <Card sx={{ mb: 3, bgcolor: alpha(theme.palette.success.main, 0.08), border: `1px solid ${alpha(theme.palette.success.main, 0.25)}` }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ color: stripeCredit > 0 ? 'success.main' : 'text.secondary', mb: 1.5 }}>
                      <CreditCardIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Credit Balance
                    </Typography>
                    <Typography variant="h4" sx={{ color: stripeCredit > 0 ? 'success.main' : 'text.secondary', fontWeight: 'bold' }}>
                      ${formatCurrency(stripeCredit)}
                    </Typography>
                    {action === 'removal' && billingImpact.billing_action === 'immediate_credit' && (
                      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                        A credit of ${formatCurrency(Math.abs(billingImpact.prorated_amount || 0))} will be added to your Stripe balance upon confirmation.
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              );
            })()}

            {/* Grace Period Information */}
            {billingImpact.grace_period_info && (
              <Card sx={{ mb: 3, bgcolor: alpha(theme.palette.info.main, 0.1), border: `1px solid ${alpha(theme.palette.info.main, 0.25)}` }}>
                <CardContent>
                  <Typography variant="h6" sx={{ color: 'info.main', mb: 2 }}>
                    <ScheduleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Grace Period Information
                  </Typography>
                  <Alert severity="info" sx={{ mb: 2, bgcolor: alpha(theme.palette.info.main, 0.08), border: `1px solid ${alpha(theme.palette.info.main, 0.35)}` }}>
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>
                      The user will be removed immediately, but you have{' '}
                      <strong>{billingImpact.grace_period_info.hours_remaining} hours</strong> to reactivate them
                      without billing changes.
                    </Typography>
                  </Alert>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    If the grace period expires, a credit of{' '}
                    <strong>${formatCurrency(billingImpact.grace_period_info?.credit_amount)}</strong> will be applied to your next billing cycle.
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/* Tier Information */}
            <Card sx={{ bgcolor: 'background.paper', backgroundImage: 'none', border: 1, borderColor: 'divider' }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: 'text.primary', mb: 2 }}>
                  Billing Tier Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Tier
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 500 }}>
                      {billingImpact.tier_info?.name || 'Unknown'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Free Users
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 500 }}>
                      {billingImpact.tier_info?.free_users_limit || 0}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Base Price
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 500 }}>
                      ${billingImpact.tier_info?.base_price || 0}/{billingImpact.billing_cycle || 'monthly'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Per User Price
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 500 }}>
                      ${billingImpact.tier_info?.price_per_user || 0}/{billingImpact.billing_cycle || 'monthly'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>
        )}
      </StyledDialogContent>

      <StyledDialogActions>
        <StyledButton onClick={onClose} variant="outlined" disabled={confirming}>
          Cancel
        </StyledButton>
        <StyledButton
          onClick={handleConfirm}
          variant="contained"
          disabled={confirming}
          startIcon={confirming ? <CircularProgress size={16} /> : null}
        >
          {confirming
            ? 'Processing...'
            : isBulkOperation
            ? action === 'addition'
              ? `Add ${selectedUserIds.length} Users & Continue`
              : `Remove ${selectedUserIds.length} Users`
            : action === 'addition'
            ? 'Add User & Continue'
            : 'Remove User'}
        </StyledButton>
      </StyledDialogActions>
    </StyledDialog>
  )
}
